// Stacked pull requests on the dashboard: which pull requests build on one
// another, and how a section lays them out. Pure: the dashboard passes the
// section's rows and every row it has loaded, and renders what comes back.
//
// A pull request is stacked on another (its base) when
// - both are in the same native GitHub stack: its base is the stack member
//   with the next lower position that the dashboard has loaded; otherwise
// - its base branch is another open pull request's head branch, compared as
//   (repository, branch). The repository is the dependent's own (where its
//   base branch lives) and the other's head repository, so a fork's branch
//   counts: a pull request in the fork, or a fork pull request built on an
//   upstream branch, stacks like one in the same repository.
// Each pull request has at most one base, so stacks are trees; a cycle of
// branches is cut above its first member by repository name, then number.
// Ties elsewhere (two open pull requests on one head) are ordered the same
// way. Where a stack forks, the dependent with the most pull requests above
// it continues the stack and comes last; the other branches come first, by
// size and then repository name and number. Members of a native stack the
// dashboard has not loaded hold their positions as placeholders.
import { sameLogin } from './attention';

/** The pull request fields stacking reads (a dashboard row's DashPr has them). */
export interface StackPr {
  id: string;
  number: number;
  state: 'OPEN' | 'CLOSED' | 'MERGED';
  updatedAt: string;
  /** Lines added and deleted. */
  additions: number;
  deletions: number;
  /** The base repository: where the pull request is open and its base branch lives. */
  repository: { nameWithOwner: string };
  baseRefName: string;
  headRefName: string;
  /** Where the head branch lives; null when that repository is gone (a deleted fork). */
  headRepository: { nameWithOwner: string } | null;
  /**
   * GitHub's native stack, or null outside one (and where the GitHub host
   * has none). `entries` names members by position, perhaps not all: a
   * member listed with no pull request is one GitHub does not show the
   * viewer. Its `id` is absent from rows read before it was asked for.
   */
  stack: {
    id: string;
    size: number;
    entries: { nodes: ({ position: number; pullRequest: { id?: string; number: number } | null } | null)[] };
  } | null;
  /** Its position in `stack`, 1 nearest the base branch. */
  stackEntry: { position: number } | null;
}

export interface StackRow {
  pr: StackPr;
  /** Who the pull request waits on; null while unknown. */
  attention: readonly { login: string }[] | null;
}

interface NodeBase<R extends StackRow> {
  /**
   * Position in the stack as shown, 1..`Stack.count`: every pull request
   * comes before those stacked on it. A row of several missing members
   * takes as many positions, starting here.
   */
  index: number;
  /** 0 at the bottom of the stack, one more per pull request it builds on. */
  depth: number;
  /**
   * The pull requests stacked directly on this one. Where the stack forks,
   * the branches come first and the one that continues the stack (the most
   * pull requests above it) last.
   */
  children: StackNode<R>[];
}

/** A pull request the dashboard loaded. */
export interface PrNode<R extends StackRow> extends NodeBase<R> {
  kind: 'pr';
  row: R;
  /** In the stack but not in this section: shown as context, so the stack reads complete. */
  context: boolean;
  /** Left out by the dashboard's filter: a thin context row, counted by none of the stack's summary but `Stack.filtered`. */
  filtered: boolean;
  /** Waits on the viewer, in this section or not. */
  yours: boolean;
}

/** Consecutive members of a native stack that no section loaded: only their places are known. */
export interface MissingNode<R extends StackRow> extends NodeBase<R> {
  kind: 'missing';
  /** The stack's repository (a native stack's pull requests share one). */
  repo: string;
  /** Their native stack positions, `from` to `to` inclusive. */
  from: number;
  to: number;
  /** The numbers of the first and last, when GitHub's `entries` named them. */
  first: number | null;
  last: number | null;
  /**
   * GitHub does not show them to the viewer: `entries` lists their places
   * without a pull request, or reading them by ID found none (`hidden` of
   * `stackSection`). Otherwise they are only not loaded.
   */
  hidden: boolean;
  /** Reading them by ID needs SSO authorization (`sso` of `stackSection`): they may show once the viewer authorizes. */
  sso: boolean;
}

export type StackNode<R extends StackRow> = PrNode<R> | MissingNode<R>;

export interface Stack<R extends StackRow> {
  kind: 'stack';
  /**
   * The stack's identity, unique in a section: `native:` and its native
   * stack's ID when its bottom is in one (which outlives the bottom pull
   * request merging), else `head:` and its bottom pull request's head
   * repository, head branch and repository.
   */
  key: string;
  /** The bottom of the stack (nearest the default branch). */
  root: StackNode<R>;
  /** Pull requests in the stack: rows shown, context rows and missing members included. */
  count: number;
  /** Repositories of its pull requests, bottom-up, once each. */
  repos: string[];
  /** `updatedAt` of the section member updated most recently: the stack's sort key. */
  latest: string;
  /**
   * How many of the stack's loaded pull requests wait on the viewer: the
   * section's and its context rows (members not loaded are unknown).
   * Members the filter leaves out are not counted; see `filtered`.
   */
  needsYou: number;
  /**
   * The first of those in stack order (by `index`, so a branch shown before
   * the rest of the stack comes first): where the viewer starts. It may be
   * a context row, never one the filter leaves out.
   */
  start: PrNode<R> | null;
  /**
   * Lines added and deleted across the pull requests loaded: section and
   * context rows, whatever their state (a merged member's lines count).
   * Members not loaded are not counted, nor those the filter leaves out;
   * see `unloaded`, `hidden` and `filtered`.
   */
  additions: number;
  deletions: number;
  /** Members not loaded (placeholders) that GitHub may show, whose lines are not in the totals. */
  unloaded: number;
  /** Members GitHub does not show the viewer (`MissingNode.hidden`), not in the totals either. */
  hidden: number;
  /**
   * Loaded members the dashboard's filter leaves out (`PrNode.filtered`):
   * in their places as context, and counted by nothing above but `count`,
   * so the summary says how many there are.
   */
  filtered: number;
}

export type Entry<R extends StackRow> = { kind: 'single'; row: R } | Stack<R>;

const repoKey = (nameWithOwner: string) => nameWithOwner.toLowerCase();
const branchKey = (nameWithOwner: string, ref: string) => `${repoKey(nameWithOwner)}\n${ref}`;
/** Deterministic order: repository, then number. */
const byRepoNumber = (a: StackPr, b: StackPr) =>
  repoKey(a.repository.nameWithOwner).localeCompare(repoKey(b.repository.nameWithOwner)) || a.number - b.number;

function push<K, V>(map: Map<K, V[]>, key: K, value: V) {
  const list = map.get(key);
  if (list) list.push(value);
  else map.set(key, [value]);
}

const MISSING = 'missing:';
const placeholder = (p: StackPr) => p.id.startsWith(MISSING);

/**
 * Stand-ins for the members of the loaded pull requests' native stacks that
 * are not loaded: one per position, with the number GitHub's `entries`
 * gives, or 0. They have no branches, so only their stack places them.
 * `hidden` gets the IDs of those `entries` lists without a pull request.
 */
function placeholders(prs: readonly StackPr[], hidden: Set<string>): StackPr[] {
  const stacks = new Map<string, { size: number; repo: string; loaded: Set<number>; named: Map<number, number>; unseen: Set<number> }>();
  for (const p of prs) {
    if (!p.stack || !p.stackEntry) continue;
    const s = stacks.get(p.stack.id) ?? { size: 0, repo: p.repository.nameWithOwner, loaded: new Set(), named: new Map(), unseen: new Set() };
    s.size = Math.max(s.size, p.stack.size);
    s.loaded.add(p.stackEntry.position);
    for (const e of p.stack.entries.nodes)
      if (e?.pullRequest) s.named.set(e.position, e.pullRequest.number);
      else if (e) s.unseen.add(e.position);
    stacks.set(p.stack.id, s);
  }
  const out: StackPr[] = [];
  for (const [id, s] of stacks)
    for (let position = 1; position <= s.size; position++)
      if (!s.loaded.has(position)) {
        const pid = `${MISSING}${id}:${position}`;
        if (!s.named.has(position) && s.unseen.has(position)) hidden.add(pid);
        out.push({
          id: pid,
          number: s.named.get(position) ?? 0,
          state: 'OPEN',
          updatedAt: '',
          additions: 0,
          deletions: 0,
          repository: { nameWithOwner: s.repo },
          baseRefName: '',
          headRefName: '',
          headRepository: null,
          stack: { id, size: s.size, entries: { nodes: [] } },
          stackEntry: { position },
        });
      }
  return out;
}

/** A native stack member to read by ID: one its loaded members' `entries` name, itself not among them. */
export interface UnloadedMember {
  id: string;
  repo: string;
  number: number;
}

/**
 * The members of `prs`' native stacks that `entries` names with an ID but
 * that are not among `prs`, once each, by stack and position.
 */
export function unloadedMembers(prs: readonly StackPr[]): UnloadedMember[] {
  const have = new Set(prs.map((p) => p.id));
  const seen = new Set<string>();
  const out: (UnloadedMember & { stack: string; position: number })[] = [];
  for (const p of prs)
    for (const e of p.stack?.entries.nodes ?? []) {
      const id = e?.pullRequest?.id;
      if (!e || !id || have.has(id) || seen.has(id)) continue;
      seen.add(id);
      out.push({ id, repo: p.repository.nameWithOwner, number: e.pullRequest!.number, stack: p.stack!.id, position: e.position });
    }
  out.sort((a, b) => a.stack.localeCompare(b.stack) || a.position - b.position);
  return out.map(({ id, repo, number }) => ({ id, repo, number }));
}

/** A pull request's key among `stackSection`'s `hidden`: `owner/name#number`, lowercased. */
export const memberKey = (repo: string, number: number) => `${repo}#${number}`.toLowerCase();

/** Each pull request's base among `prs` (by ID), with cycles cut. */
function bases(prs: readonly StackPr[]): Map<string, StackPr> {
  const byHead = new Map<string, StackPr[]>();
  const byStack = new Map<string, StackPr[]>();
  for (const p of prs) {
    if (p.state === 'OPEN' && p.headRepository) push(byHead, branchKey(p.headRepository.nameWithOwner, p.headRefName), p);
    if (p.stack && p.stackEntry) push(byStack, p.stack.id, p);
  }
  const base = new Map<string, StackPr>();
  for (const p of prs) {
    const found = nativeBase(p, byStack) ?? (placeholder(p) ? null : branchBase(p, byHead));
    if (found) base.set(p.id, found);
  }
  cutCycles(prs, base);
  return base;
}

/** The loaded member of `p`'s native stack with the greatest position below its own. */
function nativeBase(p: StackPr, byStack: Map<string, StackPr[]>): StackPr | null {
  if (!p.stack || !p.stackEntry) return null;
  let best: StackPr | null = null;
  for (const q of byStack.get(p.stack.id) ?? [])
    if (q.stackEntry!.position < p.stackEntry.position && (!best || q.stackEntry!.position > best.stackEntry!.position)) best = q;
  return best;
}

/** The open pull request whose head is `p`'s base branch: one in `p`'s repository first, then by repository name and number. */
function branchBase(p: StackPr, byHead: Map<string, StackPr[]>): StackPr | null {
  const here = repoKey(p.repository.nameWithOwner);
  const candidates = (byHead.get(branchKey(p.repository.nameWithOwner, p.baseRefName)) ?? []).filter((q) => q.id !== p.id);
  candidates.sort((a, b) => Number(repoKey(b.repository.nameWithOwner) === here) - Number(repoKey(a.repository.nameWithOwner) === here) || byRepoNumber(a, b));
  return candidates[0] ?? null;
}

/** Remove one link from each cycle: the base of its first member by repository name and number, which becomes the bottom. */
function cutCycles(prs: readonly StackPr[], base: Map<string, StackPr>) {
  const settled = new Set<string>();
  for (const start of prs) {
    const path: StackPr[] = [];
    const onPath = new Set<string>();
    for (let cur: StackPr | undefined = start; cur && !settled.has(cur.id); cur = base.get(cur.id)) {
      if (onPath.has(cur.id)) {
        const at = cur.id;
        const cycle = path.slice(path.findIndex((x) => x.id === at));
        base.delete(cycle.reduce((lo, x) => (byRepoNumber(x, lo) < 0 ? x : lo)).id);
        break;
      }
      onPath.add(cur.id);
      path.push(cur);
    }
    for (const x of path) settled.add(x.id);
  }
}

/**
 * A section's entries: its rows, with those that share a stack gathered into
 * one stack entry. `pool` is every row the dashboard has loaded (any
 * section); stacks are found among them, and a stack's rows from outside the
 * section are its context rows. A stack shows only where at least one of its
 * pull requests is a section row. Entries keep the rows' order (most
 * recently updated first), a stack placed where its most recently updated
 * section member would be (`latest`); with nothing stacked, the entries are
 * the rows. `hidden` names (`memberKey`) native stack members not loaded
 * that GitHub does not show the viewer, and `sso` those whose read needs SSO
 * authorization.
 *
 * Under the dashboard's filter, `rows` are the section's matching rows and
 * `filtered` says which loaded rows the filter leaves out (the section's
 * own and other sections'). A stack then shows when one of `rows` is in
 * it; the members left out keep their places as context (`PrNode.filtered`)
 * and its summary counts only the others (`Stack.filtered` says how many
 * were left out).
 */
export function stackSection<R extends StackRow>(
  rows: readonly R[],
  pool: readonly R[],
  me: string,
  hidden: ReadonlySet<string> = new Set(),
  sso: ReadonlySet<string> = new Set(),
  filtered: (row: R) => boolean = () => false,
): Entry<R>[] {
  const all = new Map<string, Item<R>>();
  // The section's own copy of a pull request wins over the pool's.
  for (const r of [...rows, ...pool]) if (!all.has(r.pr.id)) all.set(r.pr.id, { pr: r.pr, row: r });
  const unseen = new Set<string>();
  for (const p of placeholders([...all.values()].map((x) => x.pr), unseen)) all.set(p.id, { pr: p, row: null });
  const named = (p: StackPr, set: ReadonlySet<string>) => !!p.number && set.has(memberKey(p.repository.nameWithOwner, p.number));
  const access = (p: StackPr): Access => (unseen.has(p.id) || named(p, hidden) ? 'hidden' : named(p, sso) ? 'sso' : null);
  const prs = [...all.values()].map((x) => x.pr);
  const base = bases(prs);
  const members = new Set(rows.map((r) => r.pr.id));

  const children = new Map<string, Item<R>[]>();
  for (const x of all.values()) {
    const b = base.get(x.pr.id);
    if (b) push(children, b.id, x);
  }
  const size = new Map<string, number>();
  const sizeOf = (id: string): number => {
    let n = size.get(id);
    if (n === undefined) {
      size.set(id, 1); // a cycle is cut before here; this only guards the recursion
      n = 1 + (children.get(id) ?? []).reduce((sum, c) => sum + sizeOf(c.pr.id), 0);
      size.set(id, n);
    }
    return n;
  };
  for (const list of children.values()) list.sort((a, b) => sizeOf(a.pr.id) - sizeOf(b.pr.id) || byRepoNumber(a.pr, b.pr));

  const bottom = (id: string): string => {
    for (let b = base.get(id); b; b = base.get(id)) id = b.id;
    return id;
  };
  const built = new Map<string, Stack<R> | null>();
  const stackOf = (rootId: string): Stack<R> | null => {
    if (built.has(rootId)) return built.get(rootId)!;
    const s = layout(all.get(rootId)!, children, members, me, access, filtered);
    built.set(rootId, s);
    return s;
  };

  const entries: Entry<R>[] = [];
  const placed = new Set<Stack<R>>();
  const keys = new Set<string>();
  for (const r of rows) {
    const s = stackOf(bottom(r.pr.id));
    if (!s) entries.push({ kind: 'single', row: r });
    else if (!placed.has(s)) {
      placed.add(s);
      // Two bottoms on one head branch (proposed to two bases) would share a key.
      if (keys.has(s.key)) s.key += `\n${s.root.kind === 'pr' ? s.root.row.pr.id : s.root.index}`;
      keys.add(s.key);
      entries.push(s);
    }
  }
  const sortKey = (e: Entry<R>) => (e.kind === 'single' ? e.row.pr.updatedAt : e.latest);
  return entries
    .map((e, i) => ({ e, i }))
    .sort((a, b) => sortKey(b.e).localeCompare(sortKey(a.e)) || a.i - b.i)
    .map((x) => x.e);
}

/**
 * The stack `row`'s pull request is in among `pool`, or null when it is on
 * its own there: the stack a dashboard section holding every one of them
 * shows it in (`stackSection`), so the dashboard and a pull request's page
 * mean the same by a pull request's stack. `row` stands for any copy of
 * its pull request in `pool`. No member is a context row.
 */
export function stackOf<R extends StackRow>(row: R, pool: readonly R[], me: string, hidden: ReadonlySet<string> = new Set(), sso: ReadonlySet<string> = new Set()): Stack<R> | null {
  const all = [row, ...pool.filter((r) => r.pr.id !== row.pr.id)];
  const holds = (n: StackNode<R>): boolean => (n.kind === 'pr' && n.row === row) || n.children.some(holds);
  return stackSection(all, all, me, hidden, sso).find((e): e is Stack<R> => e.kind === 'stack' && holds(e.root)) ?? null;
}

/** Why a member not loaded is not: GitHub does not show it, its read needs SSO authorization, or neither (null). */
type Access = 'hidden' | 'sso' | null;

/** A loaded row, or a native stack member's placeholder (no row). */
interface Item<R extends StackRow> {
  pr: StackPr;
  row: R | null;
}

/** A stack's identity: see `Stack.key`. */
function stackKey(root: StackPr): string {
  if (root.stack) return `native:${root.stack.id}`;
  const head = repoKey(root.headRepository?.nameWithOwner ?? root.repository.nameWithOwner);
  return `head:${head}\n${root.headRefName}\n${repoKey(root.repository.nameWithOwner)}`;
}

/** The stack rooted at `root`, or null when it is a lone pull request. */
function layout<R extends StackRow>(
  root: Item<R>,
  children: Map<string, Item<R>[]>,
  members: Set<string>,
  me: string,
  access: (p: StackPr) => Access,
  filtered: (row: R) => boolean,
): Stack<R> | null {
  if (!children.get(root.pr.id)?.length) return null;
  const order: StackNode<R>[] = [];
  let count = 0;
  const walk = (item: Item<R>, depth: number): StackNode<R> => {
    const index = count + 1;
    const p = item.pr;
    let node: StackNode<R>;
    let next = children.get(p.id) ?? [];
    if (item.row) {
      node = {
        kind: 'pr',
        row: item.row,
        index,
        depth,
        context: !members.has(p.id),
        // A section row is one the filter shows; anything else is asked.
        filtered: !members.has(p.id) && filtered(item.row),
        yours: !!me && !!item.row.attention?.some((m) => sameLogin(m.login, me)),
        children: [],
      };
      count++;
    } else {
      // Fold a run of missing members (each the only one on the last) into one
      // row, split where GitHub starts or stops naming them, so the row reads
      // either as numbers (linked) or as positions, and where it starts or
      // stops showing them to the viewer or asking for SSO authorization.
      let last = item;
      const why = access(p);
      while (next.length === 1 && !next[0].row && !next[0].pr.number === !p.number && access(next[0].pr) === why) {
        last = next[0];
        next = children.get(last.pr.id) ?? [];
      }
      const from = p.stackEntry!.position;
      const to = last.pr.stackEntry!.position;
      node = { kind: 'missing', repo: p.repository.nameWithOwner, from, to, first: p.number || null, last: last.pr.number || null, hidden: why === 'hidden', sso: why === 'sso', index, depth, children: [] };
      count += to - from + 1;
      item = last;
    }
    order.push(node);
    node.children = next.map((c) => walk(c, depth + 1));
    return node;
  };
  const top = walk(root, 0);
  const prs = order.filter((n): n is PrNode<R> => n.kind === 'pr');
  const hidden = order.reduce((sum, n) => sum + (n.kind === 'missing' && n.hidden ? n.to - n.from + 1 : 0), 0);
  const left = prs.filter((n) => n.filtered).length;
  const yours = prs.filter((n) => n.yours && !n.filtered);

  const repos: string[] = [];
  let latest = '';
  let additions = 0;
  let deletions = 0;
  for (const n of prs) {
    const p = n.row.pr;
    if (!repos.some((x) => repoKey(x) === repoKey(p.repository.nameWithOwner))) repos.push(p.repository.nameWithOwner);
    if (!n.context && p.updatedAt > latest) latest = p.updatedAt;
    if (n.filtered) continue;
    additions += p.additions;
    deletions += p.deletions;
  }
  return {
    kind: 'stack',
    key: stackKey(root.pr),
    root: top,
    count,
    repos,
    latest,
    needsYou: yours.length,
    start: yours[0] ?? null,
    additions,
    deletions,
    unloaded: count - prs.length - hidden,
    hidden,
    filtered: left,
  };
}

/**
 * Stacks folded to their summary row: one entry per fold, holding its
 * section's ID and the IDs of the stack's pull requests when it was folded.
 * Kept by membership, not by `Stack.key`, so a stack stays folded when its
 * bottom merges and its key changes, branch and native stacks alike.
 */
export type Folds = readonly { section: string; ids: ReadonlySet<string> }[];

/** IDs of the stack's loaded pull requests (not placeholders). */
function memberIds<R extends StackRow>(s: Stack<R>): string[] {
  const out: string[] = [];
  const walk = (n: StackNode<R>) => {
    if (n.kind === 'pr') out.push(n.row.pr.id);
    n.children.forEach(walk);
  };
  walk(s.root);
  return out;
}

const foldOf = <R extends StackRow>(section: string, s: Stack<R>) => {
  const ids = memberIds(s);
  return (f: Folds[number]) => f.section === section && ids.some((id) => f.ids.has(id));
};

/** Whether a stack in this section is folded: any of its pull requests was in a stack folded there. */
export function isFolded<R extends StackRow>(folds: Folds, section: string, s: Stack<R>): boolean {
  return folds.some(foldOf(section, s));
}

/** Fold an open stack (recording its pull requests), or unfold a folded one (forgetting every fold it matches). */
export function toggleFold<R extends StackRow>(folds: Folds, section: string, s: Stack<R>): Folds {
  const matches = foldOf(section, s);
  return folds.some(matches) ? folds.filter((f) => !matches(f)) : [...folds, { section, ids: new Set(memberIds(s)) }];
}
